import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import config from '../config';
import { getOrganizationContext } from '../orgContext';

interface DashboardSummaryResponse {
  organizationName: string;
  metrics: {
    categories: number;
    activeCategories: number;
    pendingApprovalCategories: number;
    disabledCategories: number;
    courses: number;
    activeCourses: number;
    classrooms: number;
    activeClassrooms: number;
    exams: number;
    activeExams: number;
    questions: number;
    activeQuestions: number;
    assignments: number;
    publishedAssignments: number;
    draftAssignments: number;
    overdueAssignments: number;
    activeMembers: number;
    pendingInvites: number;
    pendingJoinRequests: number;
  };
  recentActivity: Array<{
    type: string;
    title: string;
    updatedAt: string;
  }>;
  suggestedActions: string[];
}

interface StatCardModel {
  label: string;
  value: number;
  accent: 'default' | 'active' | 'draft' | 'archived';
  hint: string;
}

const getStepCompletion = (metrics: DashboardSummaryResponse['metrics'] | null | undefined, key: string) => {
  if (!metrics) {
    return false;
  }

  switch (key) {
    case 'team':
      return metrics.activeMembers > 1 || metrics.pendingInvites > 0 || metrics.pendingJoinRequests > 0;
    case 'category':
      return metrics.categories > 0;
    case 'course':
      return metrics.courses > 0;
    case 'classroom':
      return metrics.classrooms > 0;
    case 'questions':
      return metrics.questions > 0;
    case 'assessment':
      return metrics.assignments > 0 || metrics.exams > 0;
    default:
      return false;
  }
};

const quickCards = [
  {
    title: 'Setup',
    description: 'Keep organizations, memberships, and role access aligned.',
    links: [
      { to: '/manage-organizations', label: 'Organizations' },
      { to: '/manage-platform-user-roles', label: 'Platform roles' },
    ],
  },
  {
    title: 'Learning content',
    description: 'Create and refine categories, courses, classrooms, and questions.',
    links: [
      { to: '/manage-categories', label: 'Categories' },
      { to: '/manage-courses', label: 'Courses' },
      { to: '/manage-questions', label: 'Questions' },
    ],
  },
  {
    title: 'Assessments',
    description: 'Manage exams and assignments from a single command center.',
    links: [
      { to: '/manage-exams', label: 'Exams' },
      { to: '/manage-assignments', label: 'Assignments' },
      { to: '/manage-schedule', label: 'Schedule' },
    ],
  },
  {
    title: 'Resources',
    description: 'Keep books, documents, and media assets ready for learners.',
    links: [
      { to: '/books', label: 'Books' },
      { to: '/manage-documents', label: 'Documents' },
      { to: '/manage-free-resources', label: 'Free resources' },
    ],
  },
];

const formatDate = (value: string): string => {
  if (!value) {
    return '—';
  }

  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const AdminDashboard: React.FC = () => {
  const [summary, setSummary] = useState<DashboardSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [welcomeMessage, setWelcomeMessage] = useState<string | null>(null);

  const { getAccessTokenSilently } = useAuth0();
  const organizationContext = getOrganizationContext();
  const organizationId = organizationContext?.organizationId;

  useEffect(() => {
    try {
      const storedMessage = window.sessionStorage.getItem('exammasters:org-onboarding:just-created');
      if (storedMessage) {
        const parsed = JSON.parse(storedMessage) as { organizationName?: string };
        setWelcomeMessage(`Your workspace ${parsed.organizationName || 'is ready'} — here are the first steps to get everyone started.`);
        window.sessionStorage.removeItem('exammasters:org-onboarding:just-created');
      }
    } catch {
      setWelcomeMessage(null);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadSummary = async () => {
      if (!organizationId) {
        setLoading(false);
        setError('Select an organization to view its dashboard.');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const token = await getAccessTokenSilently();
        const response = await axios.get<DashboardSummaryResponse>(`${config.apiBaseUrl}/api/admin/dashboard/summary`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Organization-Id': String(organizationId),
          },
        });

        if (!cancelled) {
          setSummary(response.data);
        }
      } catch (err) {
        if (!cancelled) {
          setError('Unable to load the dashboard summary right now.');
          console.error(err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadSummary();
    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently, organizationId]);

  const statCards = useMemo<StatCardModel[]>(() => {
    if (!summary) {
      return [];
    }

    return [
      {
        label: 'Active categories',
        value: summary.metrics.activeCategories,
        accent: 'active',
        hint: `${summary.metrics.pendingApprovalCategories} pending approval`,
      },
      {
        label: 'Active courses',
        value: summary.metrics.activeCourses,
        accent: 'default',
        hint: `${summary.metrics.courses} total`,
      },
      {
        label: 'Active exams',
        value: summary.metrics.activeExams,
        accent: 'default',
        hint: `${summary.metrics.exams} total`,
      },
      {
        label: 'Active questions',
        value: summary.metrics.activeQuestions,
        accent: 'default',
        hint: `${summary.metrics.questions} total`,
      },
      {
        label: 'Published assignments',
        value: summary.metrics.publishedAssignments,
        accent: 'draft',
        hint: `${summary.metrics.draftAssignments} drafts`,
      },
      {
        label: 'Overdue assignments',
        value: summary.metrics.overdueAssignments,
        accent: 'archived',
        hint: `${summary.metrics.assignments} total`,
      },
    ];
  }, [summary]);

  const onboardingSteps = useMemo(() => {
    if (!summary) {
      return [];
    }

    return [
      {
        key: 'team',
        title: 'Invite your team',
        description: 'Bring in teachers, parents, or principals and manage invites and requests to get everyone started.',
        to: '/manage-organization-requests',
        cta: 'Manage invites',
        isComplete: getStepCompletion(summary.metrics, 'team'),
      },
      {
        key: 'category',
        title: 'Create your first category',
        description: 'Shape the curriculum by creating a category that matches your teaching structure.',
        to: '/manage-categories',
        cta: 'Open categories',
        isComplete: getStepCompletion(summary.metrics, 'category'),
      },
      {
        key: 'course',
        title: 'Create a course',
        description: 'Group content into a course so learners can access it in a structured way.',
        to: '/manage-courses',
        cta: 'Open courses',
        isComplete: getStepCompletion(summary.metrics, 'course'),
      },
      {
        key: 'classroom',
        title: 'Create a classroom',
        description: 'Organize learners into classrooms and keep your teaching groups connected.',
        to: '/manage-classrooms',
        cta: 'Open classrooms',
        isComplete: getStepCompletion(summary.metrics, 'classroom'),
      },
      {
        key: 'questions',
        title: 'Add questions',
        description: 'Build practice material and assessments by adding your first set of questions.',
        to: '/manage-questions',
        cta: 'Open questions',
        isComplete: getStepCompletion(summary.metrics, 'questions'),
      },
      {
        key: 'assessment',
        title: 'Publish an assessment',
        description: 'Launch your first assignment or exam so learners can get started quickly.',
        to: '/manage-assignments',
        cta: 'Open assignments',
        isComplete: getStepCompletion(summary.metrics, 'assessment'),
      },
    ];
  }, [summary]);

  const completedSteps = onboardingSteps.filter((step) => step.isComplete).length;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ border: '1px solid #dfe8f8', borderRadius: 16, padding: 20, background: 'linear-gradient(135deg, #f7fbff 0%, #eef5ff 100%)' }}>
        <div style={{ fontSize: '0.8rem', fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#4f6ea8', marginBottom: 8 }}>
          Admin dashboard
        </div>
        <h2 style={{ margin: '0 0 8px', fontSize: '1.35rem', color: '#142541' }}>
          {summary?.organizationName ? `Welcome back to ${summary.organizationName}` : 'Run your teaching workspace from one place'}
        </h2>
        <p style={{ margin: 0, color: '#5f6f89', maxWidth: 760, lineHeight: 1.6 }}>
          Keep content, assessments, and admin tasks moving with a quick view of what needs attention in this workspace.
        </p>
        {welcomeMessage ? (
          <div style={{ marginTop: 12, border: '1px solid #d7f4e5', borderRadius: 12, padding: '10px 12px', background: '#f5fff9', color: '#166534', fontWeight: 600 }}>
            {welcomeMessage}
          </div>
        ) : null}
      </div>

      {loading ? (
        <div style={{ border: '1px solid #dfe8f8', borderRadius: 14, padding: 16, background: '#fff', color: '#5f6f89' }}>
          Loading dashboard summary…
        </div>
      ) : error ? (
        <div style={{ border: '1px solid #dfe8f8', borderRadius: 14, padding: 16, background: '#fff', color: '#b91c1c' }}>
          {error}
        </div>
      ) : null}

      {summary && !loading && !error ? (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            {statCards.map((card) => (
              <div key={card.label} style={{ border: '1px solid #dfe8f8', borderRadius: 14, padding: 16, background: '#fff', display: 'grid', gap: 6 }}>
                <div style={{ fontSize: '0.88rem', color: '#5f6f89' }}>{card.label}</div>
                <div style={{ fontSize: '1.45rem', fontWeight: 800, color: '#142541' }}>{card.value}</div>
                <div style={{ fontSize: '0.8rem', color: '#4f6ea8' }}>{card.hint}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'minmax(0, 1.7fr) minmax(320px, 1fr)' }}>
            <div style={{ border: '1px solid #dfe8f8', borderRadius: 14, padding: 16, background: '#fff', display: 'grid', gap: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div>
                  <div style={{ fontSize: '1rem', fontWeight: 800, color: '#142541' }}>Getting started</div>
                  <div style={{ fontSize: '0.9rem', color: '#5f6f89', marginTop: 4, lineHeight: 1.5 }}>
                    Follow the core setup steps for this workspace and keep the onboarding moving.
                  </div>
                </div>
                <div style={{ border: '1px solid #d7f4e5', borderRadius: 999, padding: '6px 10px', background: '#f5fff9', color: '#166534', fontWeight: 800, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                  {completedSteps}/{onboardingSteps.length} complete
                </div>
              </div>
              <div style={{ display: 'grid', gap: 10 }}>
                {onboardingSteps.map((step, index) => (
                  <div key={step.key} style={{ border: '1px solid #edf2fb', borderRadius: 12, padding: 12, background: step.isComplete ? '#f8fff9' : '#f8fbff' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                        <div style={{ width: 24, height: 24, borderRadius: '50%', background: step.isComplete ? '#d7f4e5' : '#eef4ff', color: step.isComplete ? '#166534' : '#4f6ea8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem', fontWeight: 800, flexShrink: 0 }}>
                          {step.isComplete ? '✓' : index + 1}
                        </div>
                        <div>
                          <div style={{ fontSize: '0.92rem', fontWeight: 800, color: '#142541' }}>{step.title}</div>
                          <div style={{ fontSize: '0.86rem', color: '#5f6f89', marginTop: 4, lineHeight: 1.5 }}>{step.description}</div>
                        </div>
                      </div>
                      <Link
                        to={step.to}
                        style={{ textDecoration: 'none', color: '#1d4ed8', fontWeight: 700, fontSize: '0.84rem', whiteSpace: 'nowrap' }}
                      >
                        {step.cta}
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ border: '1px solid #dfe8f8', borderRadius: 14, padding: 16, background: '#fff', display: 'grid', gap: 10 }}>
              <div style={{ fontSize: '1rem', fontWeight: 800, color: '#142541' }}>Recent activity</div>
              {summary.recentActivity.length > 0 ? (
                <div style={{ display: 'grid', gap: 8 }}>
                  {summary.recentActivity.map((item) => (
                    <div key={`${item.type}-${item.title}-${item.updatedAt}`} style={{ border: '1px solid #edf2fb', borderRadius: 10, padding: 10, background: '#f8fbff' }}>
                      <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#142541' }}>{item.title}</div>
                      <div style={{ fontSize: '0.8rem', color: '#4f6ea8', marginTop: 4 }}>{item.type} • {formatDate(item.updatedAt)}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: '#5f6f89' }}>No recent activity yet.</div>
              )}
            </div>
          </div>
        </>
      ) : null}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
        {quickCards.map((card) => (
          <div key={card.title} style={{ border: '1px solid #dfe8f8', borderRadius: 14, padding: 16, background: '#fff', display: 'grid', gap: 10 }}>
            <div style={{ fontSize: '1rem', fontWeight: 800, color: '#142541' }}>{card.title}</div>
            <div style={{ fontSize: '0.9rem', color: '#5f6f89', lineHeight: 1.5 }}>{card.description}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {card.links.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  style={{ textDecoration: 'none', color: '#1d4ed8', fontWeight: 700, fontSize: '0.84rem' }}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminDashboard;
